import { useEffect, useState } from "react";
import api from "./api";

// Mode hors connexion de l'espace élève.
// - Les réponses utiles (profil, modules, formules, devoirs…) sont gardées en
//   cache et resservies quand le réseau manque.
// - L'élève peut garder des fichiers (leçons, énoncés, cours audio) sur l'appareil.
// - Les copies remises sans réseau partent dès que la connexion revient.
// Tout est verrouillé si l'appareil n'a pas joint le serveur depuis 48 h, et
// effacé à la déconnexion, au changement de compte ou à la fin de l'abonnement.

export const DELAI_HORS_CONNEXION_MS = 48 * 3600 * 1000;
const CLE_META = "ql_hors_connexion";
const NOM_BASE = "ql_hors_connexion";
const EVENEMENT = "ql-hors-connexion";

// ── État de validité (localStorage) ──────────────────────────────────────────

function lireMeta() {
  try { return JSON.parse(localStorage.getItem(CLE_META) || "null"); } catch { return null; }
}

function ecrireMeta(meta) {
  try { localStorage.setItem(CLE_META, JSON.stringify(meta)); } catch { /* stockage indisponible */ }
}

function signaler() {
  try { window.dispatchEvent(new Event(EVENEMENT)); } catch { /* hors navigateur */ }
}

// Tolérance pour les petits recalages automatiques de l'horloge.
const TOLERANCE_HORLOGE_MS = 10 * 60 * 1000;

// Contact réussi avec le serveur : l'accès hors connexion repart pour 48 h
// et le contrôle de l'horloge repart de l'heure actuelle.
export function prolongerAccesHorsConnexion() {
  const meta = lireMeta();
  if (!meta?.userId) return;
  const maintenant = Date.now();
  ecrireMeta({ ...meta, valideJusqua: maintenant + DELAI_HORS_CONNEXION_MS, horlogeMax: maintenant, horlogeReculee: false });
}

// Fin d'accès : 48 h après le dernier contact, ou la fin de l'abonnement si elle arrive avant.
export function finAccesHorsConnexion() {
  const meta = lireMeta();
  if (!meta?.valideJusqua) return null;
  const fin = meta.finAbonnement ? Math.min(meta.valideJusqua, meta.finAbonnement) : meta.valideJusqua;
  return new Date(fin);
}

// Raison du verrou : null (accès ouvert), « delai », « abonnement » ou « horloge ».
export function verrouHorsConnexion() {
  const meta = lireMeta();
  if (!meta?.userId) return "delai";
  const maintenant = Date.now();
  // Horloge reculée (pour prolonger l'accès) : verrouillé jusqu'au prochain contact avec le serveur.
  if (meta.horlogeReculee || maintenant < Number(meta.horlogeMax || 0) - TOLERANCE_HORLOGE_MS) {
    if (!meta.horlogeReculee) ecrireMeta({ ...meta, horlogeReculee: true });
    return "horloge";
  }
  if (maintenant > Number(meta.horlogeMax || 0)) ecrireMeta({ ...meta, horlogeMax: maintenant });
  if (meta.finAbonnement && maintenant >= meta.finAbonnement) return "abonnement";
  if (maintenant >= Number(meta.valideJusqua)) return "delai";
  return null;
}

export function accesHorsConnexionValide() {
  return verrouHorsConnexion() === null;
}

export const MESSAGES_VERROU = {
  delai: "Contenus hors connexion verrouillés : connectez-vous à Internet (au moins une fois toutes les 48 h).",
  abonnement: "Votre abonnement est arrivé à échéance : renouvelez-le pour retrouver vos contenus.",
  horloge: "La date de l'appareil a été reculée : connectez-vous à Internet pour débloquer vos contenus.",
};

// Se met à jour à l'instant où l'accès se ferme (fin des 48 h ou de l'abonnement),
// même si l'application reste ouverte.
export function useVerrouHorsConnexion() {
  const version = useChangementsHorsConnexion();
  const [verrou, setVerrou] = useState(verrouHorsConnexion());
  useEffect(() => {
    setVerrou(verrouHorsConnexion());
    const fin = finAccesHorsConnexion();
    const minuterie = setInterval(() => setVerrou(verrouHorsConnexion()), fin ? Math.min(60000, Math.max(1000, fin - Date.now() + 500)) : 60000);
    return () => clearInterval(minuterie);
  }, [version]);
  return verrou;
}

// Le serveur n'a pas répondu du tout (pas de réseau, serveur injoignable).
// Une réponse 401/403/500 n'en est pas une : on ne ressert jamais le cache dans ce cas.
export function erreurReseau(erreur) {
  if (erreur?.response) return false;
  return erreur?.code === "ERR_NETWORK" || erreur?.code === "ECONNABORTED"
    || /network/i.test(erreur?.message || "") || (typeof navigator !== "undefined" && navigator.onLine === false);
}

// Wi-Fi sans Internet : le navigateur se croit en ligne. Quand le serveur ne
// répond plus, on le note et on réessaie toutes les 30 s jusqu'à son retour.
let serveurInjoignable = false;
let sondage = null;

function noterServeur(joignable) {
  if (serveurInjoignable === !joignable) return;
  serveurInjoignable = !joignable;
  clearInterval(sondage);
  sondage = null;
  if (serveurInjoignable) {
    sondage = setInterval(() => {
      api.get("classes/").then(() => noterServeur(true)).catch(() => {});
    }, 30000);
  }
  try { window.dispatchEvent(new Event(joignable ? "online" : EVENEMENT)); } catch { /* hors navigateur */ }
}

export function estEnLigne() {
  return (typeof navigator === "undefined" || navigator.onLine !== false) && !serveurInjoignable;
}

export function useEnLigne() {
  const [enLigne, setEnLigne] = useState(estEnLigne());
  useEffect(() => {
    const maj = () => setEnLigne(estEnLigne());
    window.addEventListener("online", maj);
    window.addEventListener("offline", maj);
    window.addEventListener(EVENEMENT, maj);
    return () => {
      window.removeEventListener("online", maj);
      window.removeEventListener("offline", maj);
      window.removeEventListener(EVENEMENT, maj);
    };
  }, []);
  return enLigne;
}

// Se met à jour quand le contenu hors connexion change (fichier ajouté, copie envoyée…).
export function useChangementsHorsConnexion() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const maj = () => setVersion((v) => v + 1);
    window.addEventListener(EVENEMENT, maj);
    return () => window.removeEventListener(EVENEMENT, maj);
  }, []);
  return version;
}

// ── IndexedDB ────────────────────────────────────────────────────────────────

let ouverture = null;

function ouvrirBase() {
  if (typeof indexedDB === "undefined") return Promise.reject(new Error("IndexedDB indisponible"));
  if (!ouverture) {
    ouverture = new Promise((resolve, reject) => {
      const demande = indexedDB.open(NOM_BASE, 1);
      demande.onupgradeneeded = () => {
        const base = demande.result;
        if (!base.objectStoreNames.contains("donnees")) base.createObjectStore("donnees", { keyPath: "cle" });
        if (!base.objectStoreNames.contains("fichiers")) base.createObjectStore("fichiers", { keyPath: "cle" });
        if (!base.objectStoreNames.contains("envois")) base.createObjectStore("envois", { keyPath: "id", autoIncrement: true });
      };
      demande.onsuccess = () => {
        const base = demande.result;
        base.onversionchange = () => { base.close(); ouverture = null; };
        resolve(base);
      };
      demande.onerror = () => { ouverture = null; reject(demande.error); };
    });
  }
  return ouverture;
}

async function operation(magasin, mode, action) {
  const base = await ouvrirBase();
  return new Promise((resolve, reject) => {
    const transaction = base.transaction(magasin, mode);
    const requete = action(transaction.objectStore(magasin));
    let resultat;
    if (requete) requete.onsuccess = () => { resultat = requete.result; };
    transaction.oncomplete = () => resolve(resultat);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

const lire = (magasin, cle) => operation(magasin, "readonly", (s) => s.get(cle));
const tout = (magasin) => operation(magasin, "readonly", (s) => s.getAll());
const ecrire = (magasin, valeur) => operation(magasin, "readwrite", (s) => s.put(valeur));
const effacer = (magasin, cle) => operation(magasin, "readwrite", (s) => s.delete(cle));

// ── Session ──────────────────────────────────────────────────────────────────

export function estGenerateur(profil) {
  return !!(profil?.is_enseignant || profil?.is_generateur);
}

// Ont un espace hors connexion : les élèves abonnés et, dans l'application
// Windows/Android (`appliGenerateur`), les générateurs d'épreuves.
export function compteEligible(profil, { appliGenerateur = false } = {}) {
  if (!profil?.id || profil.is_staff || profil.is_superuser || profil.is_comptable || profil.is_community_manager || profil.is_secretaire || profil.is_brand_ambassador) return false;
  if (estGenerateur(profil)) return appliGenerateur;
  return !!profil.subscription_active;
}

// Appelé après chaque /me/ réussi.
export async function memoriserProfil(profil, options) {
  if (!compteEligible(profil, options)) {
    if (lireMeta()) await purgerHorsConnexion();
    return;
  }
  const meta = lireMeta();
  if (meta?.userId && meta.userId !== profil.id) await purgerHorsConnexion();
  const maintenant = Date.now();
  const finAbonnement = !estGenerateur(profil) && profil.subscription_end_date ? new Date(profil.subscription_end_date).getTime() : null;
  ecrireMeta({
    userId: profil.id, valideJusqua: maintenant + DELAI_HORS_CONNEXION_MS, horlogeMax: maintenant,
    finAbonnement: Number.isFinite(finAbonnement) ? finAbonnement : null,
  });
  try { await ecrire("donnees", { cle: "me/", data: profil, date: Date.now() }); } catch { /* stockage indisponible */ }
}

// Profil gardé, servi au démarrage sans réseau (null si plus de 48 h).
export async function profilHorsConnexion() {
  if (!accesHorsConnexionValide()) return null;
  try {
    const entree = await lire("donnees", "me/");
    const profil = entree?.data;
    return profil && profil.id === lireMeta()?.userId ? profil : null;
  } catch {
    return null;
  }
}

export async function purgerHorsConnexion() {
  try { localStorage.removeItem(CLE_META); } catch { /* stockage indisponible */ }
  try {
    await Promise.all(["donnees", "fichiers", "envois"].map((m) => operation(m, "readwrite", (s) => s.clear())));
  } catch { /* base absente */ }
  signaler();
}

// GET qui garde sa réponse et la ressert hors connexion (pendant 48 h).
export async function getAvecCache(url, config) {
  const params = config?.params ? new URLSearchParams(config.params).toString() : "";
  const cle = params ? `${url}${url.includes("?") ? "&" : "?"}${params}` : url;
  try {
    const reponse = await api.get(url, config);
    noterServeur(true);
    if (lireMeta()?.userId) {
      prolongerAccesHorsConnexion();
      ecrire("donnees", { cle, data: reponse.data, date: Date.now() }).catch(() => {});
    }
    return reponse;
  } catch (erreur) {
    if (erreurReseau(erreur)) noterServeur(false);
    if (erreurReseau(erreur) && accesHorsConnexionValide()) {
      const entree = await lire("donnees", cle).catch(() => null);
      if (entree) return { data: entree.data, horsConnexion: true, date: entree.date };
    }
    throw erreur;
  }
}

// ── Fichiers gardés sur l'appareil ───────────────────────────────────────────

const SITE = (process.env.REACT_APP_WEB_BASE_URL || "https://www.quicklearning-cmr.com").replace(/\/$/, "");

function adresseAbsolue(url) {
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  if (/^https?:/.test(window.location.origin) && !/localhost|127\.0\.0\.1/.test(window.location.origin)) {
    return new URL(url, window.location.origin).toString();
  }
  return `${SITE}${url.startsWith("/") ? "" : "/"}${url}`;
}

function memeSite(url) {
  try {
    const hote = new URL(url).hostname;
    return hote === window.location.hostname || hote === new URL(SITE).hostname || hote.endsWith("quicklearning-cmr.com");
  } catch {
    return false;
  }
}

// Télécharge le fichier et le garde. `cle` identifie le contenu (ex. « lecon:12 »)
// car l'adresse du fichier peut changer d'une requête à l'autre.
export async function garderHorsConnexion({ cle, url, titre, type = "pdf", rubrique, details = {} }) {
  if (!accesHorsConnexionValide()) throw new Error("Connectez-vous pour activer le mode hors connexion.");
  let blob;
  if (/^\/api\//.test(url)) {
    // Fichier protégé de l'API (épreuves du générateur) : jeton renouvelé au besoin.
    blob = (await api.get(url.replace(/^\/api\//, ""), { responseType: "blob" })).data;
  } else {
    const adresse = adresseAbsolue(url);
    const jeton = localStorage.getItem("access_token");
    const reponse = await fetch(adresse, { headers: jeton && memeSite(adresse) ? { Authorization: `Bearer ${jeton}` } : {} });
    if (!reponse.ok) throw new Error(`Téléchargement impossible (${reponse.status}).`);
    blob = await reponse.blob();
  }
  if (!blob.size || /text\/html/i.test(blob.type)) throw new Error("Le fichier reçu est vide ou invalide.");
  await ecrire("fichiers", { cle, titre, type, rubrique, details, taille: blob.size, date: Date.now(), blob });
  signaler();
  return true;
}

export async function retirerHorsConnexion(cle) {
  await effacer("fichiers", cle);
  signaler();
}

// Fichier gardé, ou null (absent ou plus de 48 h sans connexion).
export async function fichierHorsConnexion(cle) {
  if (!cle || !accesHorsConnexionValide()) return null;
  try { return (await lire("fichiers", cle)) || null; } catch { return null; }
}

export async function estGardeHorsConnexion(cle) {
  if (!cle) return false;
  try { return !!(await lire("fichiers", cle)); } catch { return false; }
}

export async function listerHorsConnexion() {
  try {
    const fichiers = await tout("fichiers");
    return fichiers.map(({ blob, ...reste }) => reste).sort((a, b) => b.date - a.date);
  } catch {
    return [];
  }
}

// ── Copies remises sans réseau ───────────────────────────────────────────────

// `nature` : « devoirs » ou « evaluations ».
export async function mettreCopieEnAttente({ nature, id, titre, content = "", fichier = null }) {
  const enregistrement = {
    nature, cible: id, titre, content,
    fichier: fichier ? { blob: fichier, nom: fichier.name || "copie", type: fichier.type || "" } : null,
    remiseLe: new Date().toISOString(), userId: lireMeta()?.userId || null, erreur: "",
  };
  const cle = await ecrire("envois", enregistrement);
  noterServeur(false); // l'envoi partira dès que le serveur répondra de nouveau
  signaler();
  return cle;
}

export async function copiesEnAttente() {
  try {
    const envois = await tout("envois");
    const userId = lireMeta()?.userId;
    return envois.filter((e) => !userId || !e.userId || e.userId === userId);
  } catch {
    return [];
  }
}

export async function annulerCopieEnAttente(id) {
  await effacer("envois", id);
  signaler();
}

let envoiEnCours = null;

// Envoie les copies en attente. Renvoie { envoyees, restantes, refusees }.
export function envoyerCopiesEnAttente() {
  if (envoiEnCours) return envoiEnCours;
  envoiEnCours = (async () => {
    const bilan = { envoyees: [], restantes: 0, refusees: [] };
    const envois = await copiesEnAttente();
    for (const envoi of envois) {
      if (envoi.erreur) continue; // déjà signalée à l'élève
      const fd = new FormData();
      if (envoi.content) fd.append("content", envoi.content);
      if (envoi.fichier) {
        const fichier = new File([envoi.fichier.blob], envoi.fichier.nom, { type: envoi.fichier.type });
        fd.append("file", fichier);
      }
      fd.append("remis_hors_connexion_le", envoi.remiseLe);
      try {
        await api.post(`${envoi.nature}/${envoi.cible}/submit/`, fd, {
          transformRequest: [(data, headers) => { delete headers["Content-Type"]; return data; }],
        });
        await effacer("envois", envoi.id);
        bilan.envoyees.push(envoi);
      } catch (erreur) {
        if (erreurReseau(erreur) || erreur?.response?.status >= 500 || erreur?.response?.status === 401) {
          bilan.restantes += 1;
          continue;
        }
        const message = erreur?.response?.data?.detail || "Copie refusée par le serveur.";
        await ecrire("envois", { ...envoi, erreur: message }).catch(() => {});
        bilan.refusees.push({ ...envoi, erreur: message });
      }
    }
    if (envois.length) signaler();
    return bilan;
  })().finally(() => { envoiEnCours = null; });
  return envoiEnCours;
}
